import { Action, Root, Title } from '@radix-ui/react-toast';
import { useCallback, useContext, useLayoutEffect, useState } from 'react';
import { ClickableIcon } from '@zenid/web';
import { flex } from '../../../styled-system/patterns';
import { css } from '../../../styled-system/css';
import {
  type SnackbarMessage,
  durationForEachNotificationLevel,
  SnackbarContext,
} from '#/utils/snackbar/snackbarProvider';

const SNACKBAR_DISPLAY_STATUS = {
  visible: 'visible',
  hiding: 'hiding',
  hidden: 'hidden',
} as const;

type SnackbarDisplayStatus = (typeof SNACKBAR_DISPLAY_STATUS)[keyof typeof SNACKBAR_DISPLAY_STATUS];

type SnackbarProps = SnackbarMessage;

export function Snackbar({ id, message, notificationLevel, hideCloseButon = false }: SnackbarProps) {
  const snackbar = useContext(SnackbarContext);
  const [snackbarDisplay, setSnackbarDisplay] = useState<SnackbarDisplayStatus>(SNACKBAR_DISPLAY_STATUS.visible);
  const hideSnackbar = useCallback(() => {
    if (snackbarDisplay === SNACKBAR_DISPLAY_STATUS.hiding) {
      setSnackbarDisplay(SNACKBAR_DISPLAY_STATUS.hidden);
    }
  }, [snackbarDisplay]);
  const onOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setTimeout(() => {
          setSnackbarDisplay(SNACKBAR_DISPLAY_STATUS.hiding);
        }, durationForEachNotificationLevel[notificationLevel]);
      } else {
        setSnackbarDisplay(SNACKBAR_DISPLAY_STATUS.hiding);
      }
    },
    [notificationLevel]
  );

  useLayoutEffect(() => {
    if (snackbarDisplay === SNACKBAR_DISPLAY_STATUS.hidden) {
      snackbar.remove(id);
    }
  }, [snackbarDisplay, snackbar, id]);
  return (
    <Root
      open={snackbarDisplay === SNACKBAR_DISPLAY_STATUS.visible || snackbarDisplay === SNACKBAR_DISPLAY_STATUS.hiding}
      onOpenChange={onOpenChange}
      className={css({
        position: 'relative',
        width: 'fit-content',
        minWidth: '360px',
        minHeight: '48px',
        padding: '{spacing.12} {spacing.48} {spacing.12} {spacing.16}',
        backgroundColor: '{colors.text.sub}',
        borderRadius: '{spacing.8}',
        boxShadow: '{shadows.dropShadow}',
        animation:
          snackbarDisplay === SNACKBAR_DISPLAY_STATUS.visible
            ? '{animations.fadein}'
            : snackbarDisplay === SNACKBAR_DISPLAY_STATUS.hiding
              ? '{animations.fadeout}'
              : undefined,
      })}
      onAnimationEnd={hideSnackbar}
    >
      <Title className={css({ textStyle: 'body.m.default', color: '{colors.text.white}' })}>{message}</Title>
      {hideCloseButon || (
        <Action altText="閉じるボタン" asChild>
          <ClickableIcon
            className={flex({
              justifyContent: 'center',
              alignItems: 'center',
              position: 'absolute',
              right: '4px',
              top: '4px',
              _hover: {
                backgroundColor: '{colors.hover.white}',
              },
            })}
            name="close"
            color="colors.text.white"
            size="m"
          />
        </Action>
      )}
    </Root>
  );
}
